import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { listAPI, categoryAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";

const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("recent");

  // Dynamic page title
  usePageTitle(category?.name);

  useEffect(() => {
    loadCategory();
  }, [slug]);

  useEffect(() => {
    if (category) {
      loadLists();
    }
  }, [category, sortBy]);

  const loadCategory = async () => {
    try {
      const response = await categoryAPI.getCategoryBySlug(slug);
      setCategory(response.data);
    } catch (error) {
      console.error("Failed to load category:", error);
    }
  };

  const loadLists = async () => {
    setLoading(true);
    try {
      const response = await listAPI.getListsByCategory(
        category.id,
        0,
        50,
        sortBy
      );
      setLists(response.data.content);
    } catch (error) {
      console.error("Failed to load lists:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Category Header */}
      <div className="mb-8">
        <Link
          to="/"
          className="text-blue-400 hover:text-blue-300 mb-4 inline-block"
        >
          ← Back to Home
        </Link>
        <h1 className="text-4xl font-bold text-white mb-2">{category.name}</h1>
        {category.description && (
          <p className="text-gray-400">{category.description}</p>
        )}
        <p className="text-gray-500 mt-2">
          {lists.length} lists in this category
        </p>
      </div>

      {/* Sort Options */}
      <div className="mb-6 flex justify-end">
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-dark-card border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
        >
          <option value="recent">Recently Added</option>
          <option value="popularity">Most Popular</option>
          <option value="most-items">Most Items</option>
          <option value="fewest-items">Fewest Items</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </select>
      </div>

      {/* Lists Grid */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading...</div>
      ) : lists.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          No lists found in this category.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lists.map((list) => (
            <Link
              key={list.id}
              to={`/list/${list.slug}`}
              className="bg-dark-card hover:bg-dark-hover border border-gray-700 p-6 rounded-lg transition group"
            >
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition">
                {list.title}
              </h3>

              {list.description && (
                <div className="text-gray-400 text-sm mb-4 line-clamp-2 prose prose-invert prose-sm">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => <span {...props} />,
                      strong: ({ node, ...props }) => (
                        <strong className="text-white" {...props} />
                      ),
                      em: ({ node, ...props }) => <em {...props} />,
                    }}
                  >
                    {list.description}
                  </ReactMarkdown>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-4">
                {list.categories
                  .filter((cat) => cat.name.toLowerCase() !== "ranked")
                  .map((cat) => (
                    <span
                      key={cat.id}
                      className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded"
                    >
                      {cat.name}
                    </span>
                  ))}
                {list.isRanked && (
                  <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded">
                    Ranked
                  </span>
                )}
              </div>

              <div className="flex justify-between items-center text-sm text-gray-400 mb-2">
                <span>{list.totalItems} items</span>
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <span>
                    {list.popularity} {list.popularity === 1 ? "play" : "plays"}
                  </span>
                </div>
              </div>

              {/* Progress Indicator */}
              {list.isCompleted && (
                <div className="flex items-center gap-1.5 text-green-400 text-sm font-medium">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Completed</span>
                </div>
              )}
              {!list.isCompleted && list.isStarted && (
                <div className="flex items-center gap-1.5 text-orange-400 text-sm font-medium">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    In Progress ({list.guessedCount}/{list.totalItems})
                  </span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryPage;
